// Cây route (code-based, hợp FSD): đăng nhập → (cần phiên) cổng vào → (cổng "ready") khung ứng dụng → trang module.
// Mỗi route module chặn khi module tắt theo tenant; thiếu quyền xem ⇒ trang "chưa có quyền".
import { ErrorComponent, Outlet, createRootRouteWithContext, createRoute, createRouter, redirect, useNavigate, type ErrorComponentProps } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useMemo } from 'react';
import { startRealtime, stopRealtime, useRealtimeBindings } from '@/shared/api';
import { OverlayRegistryProvider, lazyComponent, useDocumentTitle, useStaleChunkReload } from '@/shared/lib';
import { DialogHost, Toasts } from '@/shared/ui';
import { AppShell, NoAccess } from '@/widgets/app-shell';
import { ModulePendingPage } from '@/pages/module-pending';
import { safeNext, session } from '@/entities/session';
import { bootstrapQuery, useAuthzSync, useViewer, type Bootstrap } from '@/entities/viewer';
import { MODULES, useBrandColors, type ModuleDef } from '@/entities/tenant';
import { inboxRealtime } from '@/entities/inbox';
import { notificationsRealtime } from '@/entities/notification';
import { usePresenceHeartbeat } from '@/features/presence';
import { useSessionBoundary } from '../providers/session';
import { CREATE_ITEMS, OVERLAYS, OVERLAY_REGISTRY, PAGES, REALTIME, SEARCH_PROVIDERS, TOPBAR_ITEMS } from './pages';
import { OverlayHost } from './overlays';

// Đăng nhập / cổng vào nạp theo route (biểu mẫu TanStack Form chỉ cần ở đây — người đã đăng nhập không tải); router chờ
// preload trước khi dựng route nên không nháy màn chờ.
const LoginPage = lazyComponent(() => import('@/pages/login'), (m) => m.LoginPage, 'LoginPage');
const GatePage = lazyComponent(() => import('@/pages/gate'), (m) => m.GatePage, 'GatePage');
const ShowroomPage = lazyComponent(() => import('@/pages/showroom'), (m) => m.ShowroomPage, 'ShowroomPage');

interface Ctx {
  qc: QueryClient;
}

function Root() {
  useAuthzSync();
  // Đăng nhập lại trên cùng tab (có thể là người khác) ⇒ bỏ cache của phiên trước
  useSessionBoundary();
  return (
    <>
      <Outlet />
      <DialogHost />
      <Toasts />
    </>
  );
}
// Lỗi không route nào bắt: báo như trước (ErrorComponent của router); riêng chunk cũ sau deploy (tab mở từ trước, trang /
// lớp phủ chưa nạp) ⇒ tải lại trang một lần thay cho báo lỗi.
function RootError({ error }: ErrorComponentProps) {
  return useStaleChunkReload(error) ? (
    <div className="dt-empty" role="status">
      Đang tải phiên bản mới…
    </div>
  ) : (
    <ErrorComponent error={error} />
  );
}
const root = createRootRouteWithContext<Ctx>()({ component: Root, errorComponent: RootError });

const login = createRoute({
  getParentRoute: () => root,
  path: '/dang-nhap',
  // next: đường dẫn nội bộ đang mở khi bị đưa về đây (chưa đăng nhập / phiên hết hạn) ⇒ đăng nhập xong quay lại đúng chỗ
  validateSearch: (s: Record<string, unknown>): { loi?: string; next?: string } => ({
    ...(typeof s.loi === 'string' ? { loi: s.loi } : {}),
    ...(typeof s.next === 'string' ? { next: s.next } : {}),
  }),
  beforeLoad: async ({ search }) => {
    if ((await session.restore()) === 'authenticated') throw redirect({ href: safeNext(search.next) });
  },
  component: LoginPage,
});

// Về từ Elite Accounts: gateway đã đặt cookie refresh ⇒ hoàn tất phiên rồi vào đường dẫn nội bộ đã xin (hoặc báo lỗi ở màn đăng nhập)
const loginSso = createRoute({
  getParentRoute: () => root,
  path: '/dang-nhap/sso',
  validateSearch: (s: Record<string, unknown>): { next?: string } => (typeof s.next === 'string' ? { next: s.next } : {}),
  beforeLoad: async ({ search }) => {
    if ((await session.completeSso()) === 'authenticated') throw redirect({ href: safeNext(search.next) });
    throw redirect({ to: '/dang-nhap', search: { loi: 'dang-nhap-that-bai' } });
  },
});

const authed = createRoute({
  getParentRoute: () => root,
  id: 'authed',
  beforeLoad: async ({ context, location }): Promise<{ bootstrap: Bootstrap }> => {
    if ((await session.restore()) !== 'authenticated') {
      const next = safeNext(location.href);
      throw redirect({ to: '/dang-nhap', search: next === '/' ? {} : { next } });
    }
    return { bootstrap: await context.qc.ensureQueryData(bootstrapQuery()) };
  },
});

const gate = createRoute({ getParentRoute: () => authed, path: '/bat-dau', component: GatePage });

/** Chạy nền của phiên đã vào: màu thương hiệu, về cổng vào khi bị khoá, kênh thời gian thực, nhịp hiện diện. */
function useAppRuntime() {
  const b = useViewer();
  const navigate = useNavigate();
  useBrandColors(b.tenant.brand, b.tenant.brand2);
  // Bị khoá / gỡ liên kết / nghỉ việc trong lúc đang dùng ⇒ về cổng vào tương ứng
  useEffect(() => {
    if (b.gate !== 'ready') void navigate({ to: '/bat-dau' });
  }, [b.gate, navigate]);
  // Kênh thời gian thực: mở khi vào khung ứng dụng; mỗi chủ đề ⇒ khoá cần làm mới ("inbox", "notifications" + REALTIME của
  // module bật).
  // Nối lại sau mất kết nối ⇒ làm mới một lần mọi khoá đã đăng ký.
  useEffect(() => {
    startRealtime();
    return stopRealtime;
  }, []);
  const bindings = useMemo(() => [inboxRealtime, notificationsRealtime, ...REALTIME.filter((x) => b.enabledModules.includes(x.module))], [b.enabledModules]);
  useRealtimeBindings(bindings);
  usePresenceHeartbeat();
}

function AppLayout() {
  useAppRuntime();
  return (
    <OverlayRegistryProvider registry={OVERLAY_REGISTRY}>
      <AppShell createItems={CREATE_ITEMS} searchProviders={SEARCH_PROVIDERS} topbarItems={TOPBAR_ITEMS}>
        <Outlet />
      </AppShell>
      <OverlayHost defs={OVERLAYS} />
    </OverlayRegistryProvider>
  );
}
/** Trang toàn màn hình (Trình chiếu dự án): không khung ứng dụng, vẫn có thời gian thực và lớp phủ toàn cục (drawer căn, phiếu cọc). */
function PresentLayout() {
  useAppRuntime();
  return (
    <OverlayRegistryProvider registry={OVERLAY_REGISTRY}>
      <Outlet />
      <OverlayHost defs={OVERLAYS} />
    </OverlayRegistryProvider>
  );
}
const app = createRoute({
  getParentRoute: () => authed,
  id: 'app',
  beforeLoad: ({ context }) => {
    if (context.bootstrap.gate !== 'ready') throw redirect({ to: '/bat-dau' });
  },
  component: AppLayout,
});

const present = createRoute({
  getParentRoute: () => authed,
  id: 'present',
  beforeLoad: ({ context }) => {
    if (context.bootstrap.gate !== 'ready') throw redirect({ to: '/bat-dau' });
  },
  component: PresentLayout,
});
function ShowroomView() {
  const b = useViewer();
  if (!b.visibleModules.includes('booking')) return <NoAccess />;
  return (
    <Suspense
      fallback={
        <div className="dt-empty" role="status">
          Đang tải…
        </div>
      }
    >
      <ShowroomPage />
    </Suspense>
  );
}
// Trình chiếu dự án cho sale & khách (docs/TRINH-CHIEU-DU-AN.md) — thuộc module Chốt căn
const showroomRoute = createRoute({
  getParentRoute: () => present,
  path: '/trinh-chieu',
  validateSearch: (s: Record<string, unknown>) => s,
  beforeLoad: ({ context }) => {
    if (!context.bootstrap.enabledModules.includes('booking')) throw redirect({ to: '/tong-quan' });
  },
  component: Object.assign(ShowroomView, { preload: () => ShowroomPage.preload() }),
});

const index = createRoute({
  getParentRoute: () => app,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tong-quan' });
  },
});

function ModuleView({ module }: { module: ModuleDef }) {
  const b = useViewer();
  const allowed = b.visibleModules.includes(module.id);
  // Tiêu đề tab theo trang: "<Module> · <Công ty>" (trang chưa có quyền: "Chưa có quyền · <Công ty>")
  useDocumentTitle(`${allowed ? module.label : 'Chưa có quyền'} · ${b.tenant.name}`);
  if (!allowed) return <NoAccess />;
  const Page = PAGES[module.id];
  // Trang nạp theo route (router đã chờ preload trước khi dựng ⇒ Suspense chỉ là lưới an toàn)
  return Page ? (
    <Suspense
      fallback={
        <div className="dt-empty" role="status">
          Đang tải…
        </div>
      }
    >
      <Page />
    </Suspense>
  ) : (
    <ModulePendingPage module={module} />
  );
}

const moduleRoutes = MODULES.map((m) =>
  createRoute({
    getParentRoute: () => app,
    path: `/${m.route}`,
    validateSearch: (s: Record<string, unknown>) => s,
    beforeLoad: ({ context }) => {
      if (!context.bootstrap.enabledModules.includes(m.id)) throw redirect({ to: '/tong-quan' });
    },
    // preload: router nạp chunk của trang khi điều hướng và khi rê chuột lên liên kết (defaultPreload 'intent')
    component: Object.assign(() => <ModuleView module={m} />, { preload: () => PAGES[m.id]?.preload() }),
  }),
);

const tree = root.addChildren([login, loginSso, authed.addChildren([gate, present.addChildren([showroomRoute]), app.addChildren([index, ...moduleRoutes])])]);

export function makeRouter(qc: QueryClient) {
  return createRouter({
    routeTree: tree,
    context: { qc },
    defaultPreload: 'intent',
    defaultNotFoundComponent: () => <NoAccess />,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
